import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { getAuth } from "firebase/auth";
import { db, storage, auth } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { VoucherInput } from "../common/VoucherInput";
import { PayPalButton } from "../common/PayPalButton";
import { Button, InputField } from "@cakrawalajakarta/shared-components";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faDownload,
  faCheck,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";

const SHUTTLE_COLLECTION = "pendaftaranShuttle2026";

const findRegistrationCategory = (item, eventCategories) => {
  const categories = eventCategories[item?.eventId] || [];
  return categories.find((category) =>
    category.categoryName === item?.kategori ||
    Number(category.distance) === Number(item?.distance)
  );
};

const isCategoryPaymentOpen = (item, eventCategories) => {
  if (item?.eventId !== "JOTR2026") return true;
  return findRegistrationCategory(item, eventCategories)?.isRegistrationOpen === true;
};

const getCategoryIcons = (categoryName, distance) => {
  const normName = (categoryName || "").trim().toUpperCase();
  // Map based on distance or name keywords
  if (normName.includes("FUN TRAIL") || distance == 5) {
    return { icon1Url: null, icon2Url: null, icon3Url: null };
  } else if (normName.includes("CIANJUR TRAIL RUN") || distance == 10) {
      return { icon1Url: "/icons/utmb.png", icon2Url: "/icons/itra0.svg", icon3Url: null };
  } else if (normName.includes("HM TRAIL") || distance == 21) {
      return { icon1Url: "/icons/utmb.png", icon2Url: "/icons/itra1.png", icon3Url: null };
  } else if (normName.includes("MOUNTAIN MARATHON") || distance == 42) {
      return { icon1Url: "/icons/utmb.png", icon2Url: "/icons/utmb50.png", icon3Url: "/icons/itra3.png" };
  } else if (normName.includes("ULTRA TRAIL") || distance == 75) {
      return { icon1Url: "/icons/utmb.png", icon2Url: "/icons/utmb100.svg", icon3Url: "/icons/itra4.png" };
  }
  return { icon1Url: null, icon2Url: null, icon3Url: null };
};

// Registration Status Indicator Component
// Helper to get simplified category display


const getFinalPrice = (item, appliedVoucher, eventCategories) => {
    const categories = eventCategories[item.eventId] || [];
    let categoryDetail = null;
    
    if (item.kategori) {
        categoryDetail = categories.find(c => c.categoryName === item.kategori);
    }
    if (!categoryDetail && item.distance) {
            categoryDetail = categories.find(c => c.distance == item.distance);
    }
    if (!categoryDetail && item.kategori) {
        const itemDistanceMatch = (item.kategori || "").match(/(\d+)/);
        const itemDistance = itemDistanceMatch ? parseInt(itemDistanceMatch[0]) : null;
        if (itemDistance) {
                categoryDetail = categories.find(c => c.distance == itemDistance);
        }
    }
    if (!categoryDetail && item.kategori) {
        const k = item.kategori.toUpperCase();
            if (k.includes("5K") || k.includes("FUN")) categoryDetail = categories.find(c => c.distance == 5);
            else if (k.includes("10K")) categoryDetail = categories.find(c => c.distance == 10);
            else if (k.includes("21K") || k.includes("HM")) categoryDetail = categories.find(c => c.distance == 21);
            else if (k.includes("42K") || k.includes("MARATHON")) categoryDetail = categories.find(c => c.distance == 42);
            else if (k.includes("75K") || k.includes("ULTRA")) categoryDetail = categories.find(c => c.distance == 75);
    }
    categoryDetail = categoryDetail || {};
    
    const isForeigner = item.negara && item.negara.toLowerCase() !== "indonesia";
    const baseEventPrice = isForeigner && categoryDetail.foreignerPrice 
        ? categoryDetail.foreignerPrice 
        : categoryDetail.price;

    const displayPrice = baseEventPrice || item.harga;
    let finalPrice = displayPrice;
    if (appliedVoucher && appliedVoucher.discount > 0) {
        if (appliedVoucher.discountType === "fixed") {
            finalPrice = displayPrice - appliedVoucher.discount;
        } else {
            finalPrice = displayPrice - (displayPrice * appliedVoucher.discount / 100);
        }
    }
    return { displayPrice, finalPrice: Math.max(0, finalPrice) };
};

const RegistrationStatusIndicator = ({ item }) => {
  // Determine current step: 1=Registered, 2=Qualified, 3=Paid
  const isPaid = item.transaction_status === "settlement" || item.transaction_status === "capture";
  const isQualified = item.administration_status === "qualified";
  const isRejected = item.administration_status === "rejected";
  
  if (isRejected) {
    return (
      <div className="mb-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
             <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 text-sm" />
          </div>
          <div>
            <h4 className="font-semibold text-amber-700 text-sm">Your qualification data does not meet the requirements.</h4>
            <p className="text-xs text-amber-600 mt-0.5">
             Please re-register with the appropriate category.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isRoadToJOTR = item.eventId === "ROADTOJOTR2026";
  const steps = isRoadToJOTR ? ["Registered", "Paid"] : ["Registered", "Qualified", "Paid"];

  let currentStep = 1;
  if (isPaid) {
    currentStep = steps.length;
  } else if (!isRoadToJOTR && isQualified) {
    currentStep = 2;
  }

  return (
    <div className="mb-4">
      <div className="relative flex items-center justify-between">
        {/* Connecting Line Background */}
        <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-200" />
        {/* Connecting Line Progress */}
        <div
          className="absolute top-4 left-0 h-0.5 bg-lime-500 transition-all duration-500"
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />

        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep || (stepNumber === steps.length && currentStep === steps.length);

          return (
            <div key={index} className="flex flex-col items-center relative bg-white px-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
                  isCompleted
                    ? "border-lime-500 bg-lime-500 text-white"
                    : isActive
                    ? "border-lime-500 bg-white text-lime-600"
                    : "border-gray-300 bg-white text-gray-400"
                }`}
              >
                {isCompleted ? <FontAwesomeIcon icon={faCheck} className="text-xs" /> : stepNumber}
              </div>
              <span
                className={`text-xs font-medium mt-1 transition-colors ${
                  isActive || isCompleted ? "text-lime-600" : "text-gray-400"
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const cachePesananData = (uid, data) => {
  try {
    localStorage.setItem(
      `pesanan_${uid}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("[CACHE] Failed to save cache:", err);
  }
};

const getCachedPesananData = (uid) => {
  try {
    const cached = localStorage.getItem(`pesanan_${uid}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000;
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    if (!data || !Array.isArray(data.pesanan)) {
      console.warn("[CACHE] Invalid cache: orders is not an array");
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("[CACHE] Failed to fetch cache:", err);
    if (uid) localStorage.removeItem(`pesanan_${uid}`);
    return null;
  }
};

const shortenLocation = (location) => {
  if (!location || typeof location !== "string") return "-";
  const parts = location.split(" ");
  if (parts.length === 1) return location.slice(0, 8); // Short names like "Bogor"
  if (parts[0].toLowerCase().startsWith("jakarta")) {
    return `Jkt ${parts[1]}`; // e.g., "Jakarta Timur" → "Jkt Timur"
  }
  return parts[0].slice(0, 4); // e.g., "Bandung Barat" → "Band"
};

const isBankPaymentActive = (item) => {
  if (!item || item.transaction_status !== "pending") return false;
  
  // Check if it's a bank/VA transaction
  const hasVA = (item.va_numbers && item.va_numbers.length > 0) || 
                item.permata_va_number || 
                (item.bill_key && item.biller_code);
                
  if (!hasVA) return false;

  // Check if it's expired
  if (item.expiry_time) {
      const expiry = new Date(item.expiry_time);
      if (expiry < new Date()) return false; // Expired
  }

  return true;
};

const hasUpgradePaymentDetails = (upgrade) =>
  Boolean(
    (upgrade?.va_numbers && upgrade.va_numbers.length > 0) ||
      upgrade?.permata_va_number ||
      (upgrade?.bill_key && upgrade?.biller_code)
  );

export const MyRegistration = React.memo(() => {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [pesanan, setPesanan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.pesanan : [];
  });
  const [suratPernyataan, setSuratPernyataan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.suratPernyataan : {};
  });
  const [eventTitles, setEventTitles] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.eventTitles : {};
  });
  const [eventCategories, setEventCategories] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.eventCategories || {} : {};
  });
  const [categoryTransferConfigs, setCategoryTransferConfigs] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.categoryTransferConfigs || {} : {};
  });

  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [currentVoucherInput, setCurrentVoucherInput] = useState("");
  const [appliedUpgradeVoucher, setAppliedUpgradeVoucher] = useState(null);
  const [currentUpgradeVoucherInput, setCurrentUpgradeVoucherInput] = useState("");
  const [upgradePaymentMethod, setUpgradePaymentMethod] = useState("midtrans");
  const [cancellingUpgrade, setCancellingUpgrade] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("midtrans"); // 'midtrans' or 'paypal'
  const [loading, setLoading] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return !cached; // If cached exists, not loading initially
  });
  const [error, setError] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [searchParams] = useSearchParams();
  const [notificationShown, setNotificationShown] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [transferModal, setTransferModal] = useState({
    isOpen: false,
    item: null,
    targetCategory: "",
    stravaLink: "",
    certificateUrl: "",
  });
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [transferSaving, setTransferSaving] = useState(false);
  const [modalErrors, setModalErrors] = useState({});
  const [pendingUpgrades, setPendingUpgrades] = useState([]);
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const orderId = searchParams.get("order_id");
  const status = searchParams.get("transaction_status");

  const getUpgradeFinalAmount = useCallback((upgrade) => {
    // Mirror the server: always discount from the untouched original amount.
    // `amountDue` may already carry a discount from an earlier checkout attempt,
    // which would otherwise apply the voucher twice on screen.
    const baseAmount = Number(upgrade?.originalAmountDue ?? upgrade?.amountDue ?? 0);
    if (!appliedUpgradeVoucher?.discount) return baseAmount;

    if (appliedUpgradeVoucher.discountType === "fixed") {
      return Math.max(0, baseAmount - Number(appliedUpgradeVoucher.discount || 0));
    }

    return Math.max(0, baseAmount - Math.floor(baseAmount * Number(appliedUpgradeVoucher.discount || 0) / 100));
  }, [appliedUpgradeVoucher]);

  // Fix payment method if price is 0 or if bank payment is active
  useEffect(() => {
    if (selectedOrderId) {
        const item = pesanan.find(p => (p.orderId || p.id) === selectedOrderId);
        if (item) {
            const { finalPrice } = getFinalPrice(item, appliedVoucher, eventCategories);
            
            // Priority 1: If price is 0, must use midtrans (Complete Registration)
            if (finalPrice === 0 && paymentMethod !== "midtrans") {
                setPaymentMethod("midtrans");
            }
            
            // Priority 2: If bank payment is active, must use midtrans
            if (isBankPaymentActive(item) && paymentMethod !== "midtrans") {
                setPaymentMethod("midtrans");
            }
        }
    }
  }, [selectedOrderId, appliedVoucher, pesanan, eventCategories, paymentMethod]);


  const formatDate = useCallback((date) => {
    if (!date) return "Not available";
    try {
      if (date && (date.seconds || date._seconds)) {
        const timestamp = date.seconds || date._seconds;
        return new Date(timestamp * 1000).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (typeof date === "string") {
        let parsedDate = new Date(date.replace(" at ", " "));
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
        parsedDate = new Date(date.replace(/WIB$/, "").trim());
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      console.warn("[FORMAT] Date format not recognized:", date);
      return "Not available";
    } catch (err) {
      console.error("[FORMAT] Failed to format date:", err, date);
      return "Not available";
    }
  }, []);

  const isCategoryTransferOpen = useCallback((item) => {
    const config = categoryTransferConfigs[item?.eventId || "JOTR2026"];
    if (!config?.enabled) return false;
    if (!config.deadline) return true;

    const deadline = new Date(`${config.deadline}T23:59:59.999+07:00`);
    if (Number.isNaN(deadline.getTime())) return true;
    return new Date() <= deadline;
  }, [categoryTransferConfigs]);

  const getCategoryByName = useCallback((eventId, categoryName) => {
    const categories = eventCategories[eventId] || [];
    return categories.find((category) => category.categoryName === categoryName);
  }, [eventCategories]);

  const getCategoryPriceForItem = useCallback((category, item) => {
    const country = String(item?.negara || item?.country || "").trim().toLowerCase();
    const isForeigner = country && country !== "indonesia";
    return Number(isForeigner && category?.foreignerPrice ? category.foreignerPrice : category?.price) || 0;
  }, []);

  const handleUpgradePayment = useCallback(async (upgrade) => {
    if (!window.snap) {
      showNotification("error", "Payment gateway is not ready.");
      return;
    }

    try {
      const idToken = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/category-transfer/create-transaction`,
        {
          transferOrderId: upgrade.orderId || upgrade.id,
          voucherCode: appliedUpgradeVoucher?.code || null,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const { snapToken, status } = response.data;
      if (status === "settlement") {
        localStorage.removeItem(`pesanan_${user.uid}`);
        showNotification("success", "Upgrade payment completed. Category will be updated shortly.");
        await fetchPesananDanSurat(true);
        return;
      }

      if (!snapToken) {
        throw new Error("Failed to get payment token");
      }

      window.snap.pay(snapToken, {
        onSuccess: async () => {
          localStorage.removeItem(`pesanan_${user.uid}`);
          showNotification("success", "Upgrade payment successful. Category will be updated shortly.");
          await fetchPesananDanSurat(true);
        },
        onPending: () => showNotification("warning", "Please complete the upgrade payment."),
        onError: () => showNotification("error", "Upgrade payment failed."),
        onClose: () => showNotification("info", "Upgrade payment was not completed."),
      });
    } catch (error) {
      console.error("Failed to initiate upgrade payment", error);
      showNotification("error", "Failed to initiate payment: " + (error.response?.data?.message || error.message));
    }
  }, [user, appliedUpgradeVoucher, showNotification]); // fetchPesananDanSurat accessed via closure at call time

  const handleCancelUpgrade = useCallback(async (upgrade) => {
    const confirmed = window.confirm(
      "Cancel this category upgrade request? Your original paid registration stays exactly as it is, and you can request a different category afterwards."
    );
    if (!confirmed) return;

    setCancellingUpgrade(true);
    try {
      const idToken = await getIdToken();
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/category-transfer/cancel-upgrade`,
        { transferOrderId: upgrade.orderId || upgrade.id },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      localStorage.removeItem(`pesanan_${user.uid}`);
      setAppliedUpgradeVoucher(null);
      showNotification("success", "Upgrade request cancelled. Your original registration is unchanged.");
      await fetchPesananDanSurat(true);
    } catch (error) {
      showNotification(
        "error",
        "Failed to cancel upgrade: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setCancellingUpgrade(false);
    }
  }, [user, showNotification]); // fetchPesananDanSurat accessed via closure at call time

  const handleCertificateUpload = async (file) => {
    if (!file) return;

    // Bersihkan error segera setelah file dipilih
    setModalErrors((prev) => ({ ...prev, certificateUrl: "" }));
    setUploadingCertificate(true);

    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid || "anonymous";
      const timestamp = Date.now();
      const fileName = `cert_${userId}_${timestamp}_${file.name}`;
      
      // Mengikuti pola path form asli: qualifications/{userId}/{fileName}
      const storageRef = ref(storage, `qualifications/${userId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update state transferModal dengan URL yang baru didapat
      setTransferModal((prev) => ({
        ...prev,
        certificateUrl: downloadURL,
      }));

      showNotification("success", "Certificate uploaded successfully!", "Close");
    } catch (error) {
      console.error("Upload error:", error);
      showNotification(
        "error",
        "Failed to upload certificate. Please try again.",
        "Close"
      );
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleCategoryTransfer = async () => {
    if (!transferModal.item) return;

    const newErrors = {};
    if (!transferModal.targetCategory) {
      newErrors.targetCategory = "Target category is required.";
    }

    const categories = eventCategories[transferModal.item.eventId] || [];
    const currentCategory = transferModal.item.kategori || transferModal.item.distance;
    const currentDetail = categories.find(c => c.categoryName === currentCategory) || {};
    const targetDetail = categories.find(c => c.categoryName === transferModal.targetCategory) || {};
    
    // Robust price parsing: remove any non-numeric characters (like "Rp", ".", etc.)
    const cleanPrice = (val) => {
      if (typeof val === "number") return val;
      const cleaned = String(val || "0").replace(/[^0-9]/g, "");
      return parseInt(cleaned, 10) || 0;
    };

    const currentPrice = getCategoryPriceForItem(currentDetail, transferModal.item) || cleanPrice(transferModal.item.harga);
    const targetPrice = getCategoryPriceForItem(targetDetail, transferModal.item);
    const difference = targetDetail ? targetPrice - currentPrice : 0;

    if (difference > 0) {
      if (!transferModal.stravaLink) {
        newErrors.stravaLink = "STRAVA link is required for qualification check.";
      }
      
      if (uploadingCertificate) {
        newErrors.certificateUrl = "Your certificate is still uploading. Please wait a moment...";
      } else if (!transferModal.certificateUrl) {
        newErrors.certificateUrl = "Qualification certificate is required. Please upload a file.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setModalErrors(newErrors);
      showNotification("error", "Please correct the errors before proceeding.");
      return;
    }

    setModalErrors({});
    setTransferSaving(true);
    try {

      const token = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/category-transfer/change-category`,
        {
          eventId: transferModal.item.eventId || "JOTR2026",
          orderId: transferModal.item.orderId || transferModal.item.id,
          targetCategory: transferModal.targetCategory,
          stravaLink: transferModal.stravaLink,
          certificateUrl: transferModal.certificateUrl,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.requiresAdminApproval) {
        localStorage.removeItem(`pesanan_${user.uid}`);
        setTransferModal({ isOpen: false, item: null, targetCategory: "" });
        showNotification("info", "Upgrade request submitted. Admin will review your qualification before payment is enabled.");
        await fetchPesananDanSurat(true);
      } else {
        localStorage.removeItem(`pesanan_${user.uid}`);
        setTransferModal({ isOpen: false, item: null, targetCategory: "" });
        showNotification("success", response.data?.message || "Category updated successfully.");
        await fetchPesananDanSurat(true);
      }
    } catch (error) {
      showNotification(
        "error",
        error.response?.data?.message || "Failed to change category."
      );
    } finally {
      setTransferSaving(false);
    }
  };

  const fetchPesananDanSurat = useCallback(
    async (forceRefresh = false) => {
      if (!user?.uid || fetchStatus.current.isFetching) {
        if (!user?.uid) setError("User not authenticated.");
        setLoading(false);
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setError("");

        if (!forceRefresh) {
          const cachedData = getCachedPesananData(user.uid);
          if (cachedData && isMounted.current) {
            setPesanan(cachedData.pesanan);
            setSuratPernyataan(cachedData.suratPernyataan);
            setEventTitles(cachedData.eventTitles || {});
            setEventCategories(cachedData.eventCategories || {});
            setCategoryTransferConfigs(cachedData.categoryTransferConfigs || {});
            setLoading(false);
            console.log("[FETCH] Using cached data for uid:", user.uid);
            return;
          }
        }

        // Only show loading if we are doing a network fetch AND (it's a force refresh OR we don't have data yet)
        // Actually, for simplicity/safety against stale UI, we set loading true here for network requests
        if (isMounted.current) setLoading(true);

        const pesananList = [];
        const processedOrderIds = new Set();
        const suratData = {};
        const eventTitlesData = {};
        const eventCategoriesData = {};
        const categoryTransferConfigsData = {};

        // Load event titles from semuaKegiatan (single fetch, reused below)
        let allEventsSnapshot = null;
        try {
          allEventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of allEventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            eventTitlesData[eventId] = eventData.title || eventId;
            eventCategoriesData[eventId] = eventData.categories || [];
            categoryTransferConfigsData[eventId] = eventData.categoryTransferConfig || null;
          }
          eventTitlesData["JOTR"] = "Jakarta Open Trail Run";
        } catch (err) {
          console.error("[FETCH] Failed to load semuaKegiatan data:", err);
        }

        // Load event registrations (reuse the snapshot from above)
        try {
          const eventsSnapshot = allEventsSnapshot;
          if (!eventsSnapshot) throw new Error("semuaKegiatan snapshot unavailable");
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            // Event registrations shown in My Registration.
            if (!["JOTR2026", "ROADTOJOTR2026", "FUNRUN5K2026"].includes(eventId)) continue;

            const eventData = eventDoc.data();
            const collectionName =
              eventData.collectionName || `pendaftaran${eventId}`;
            const pesananQuery = query(
              collection(db, collectionName),
              where("uid", "==", user.uid)
            );
            const pesananSnapshot = await getDocs(pesananQuery);
            pesananSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!processedOrderIds.has(doc.id)) {
                pesananList.push({
                  id: doc.id,
                  eventId,
                  collectionName,
                  kategori: data.kategori || null,
                  type: "event",
                  ...data,
                });
                processedOrderIds.add(doc.id);
              }
            });
          }
        } catch (err) {
          console.error("[FETCH] Failed to load event registrations:", err);
        }



        // Load data from orderMappings
        try {
          const orderMappingsQuery = query(
            collection(db, "orderMappings"),
            where("uid", "==", user.uid)
          );
          const orderMappingsSnapshot = await getDocs(orderMappingsQuery);
          for (const orderDoc of orderMappingsSnapshot.docs) {
            const orderData = orderDoc.data();
            const orderId = orderData.orderId;
            const eventId = orderData.eventId;
            if (processedOrderIds.has(orderId)) continue;
            // Event registrations shown in My Registration.
            if (!["JOTR2026", "ROADTOJOTR2026", "FUNRUN5K2026"].includes(eventId)) continue;
            // Cek untuk event
            const eventDocRef = doc(db, "semuaKegiatan", eventId);
            const eventDoc = await getDoc(eventDocRef);
            const collectionName = eventDoc.exists()
              ? eventDoc.data().collectionName || `pendaftaran${eventId}`
              : `pendaftaran${eventId}`;
            const registrationDocRef = doc(db, collectionName, orderId);
            const registrationDoc = await getDoc(registrationDocRef);
            if (registrationDoc.exists()) {
              const registrationData = registrationDoc.data();
              if (
                registrationData.uid === user.uid &&
                ["pending", "settlement"].includes(
                  registrationData.transaction_status
                )
              ) {
                pesananList.push({
                  id: registrationDoc.id,
                  eventId,
                  collectionName,
                  kategori: registrationData.kategori || null,
                  type: "event",
                  ...registrationData,
                });
                processedOrderIds.add(orderId);
              }
            }
            }

        } catch (err) {
          console.error("[FETCH] Failed to load orderMappings:", err);
        }

        // Load pending upgrade requests
        let upgrades = [];
        try {
          // Close anything past its window first. The gateway only reports
          // expiry for requests that reached checkout, so an abandoned request
          // would otherwise hang forever.
          try {
            const releaseToken = await getIdToken();
            await axios.post(
              `${import.meta.env.VITE_API_BASE_URL}/api/category-transfer/release-expired`,
              {},
              { headers: { Authorization: `Bearer ${releaseToken}` } }
            );
          } catch (releaseErr) {
            console.error("[FETCH] Failed to release expired upgrade requests:", releaseErr);
          }

          const upgradeQuery = query(
            collection(db, "categoryTransferPayments"),
            where("uid", "==", user.uid)
          );
          const upgradeSnapshot = await getDocs(upgradeQuery);
          const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
          upgrades = upgradeSnapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((u) => {
              if (u.applied) return false;
              if (!["pending_approval", "approved", "rejected"].includes(u.approval_status)) return false;
              // A dead gateway transaction means this request can no longer be
              // paid, even if the server never got to flip approval_status.
              if (["expire", "cancel", "deny", "failure"].includes(u.transaction_status)) return false;
              if (u.approval_status === "rejected") {
                const reviewedMs = u.reviewedAt?.toDate?.()?.getTime() ?? u.updatedAt?.toDate?.()?.getTime() ?? 0;
                return reviewedMs > fourteenDaysAgo;
              }
              return true;
            });
          if (isMounted.current) setPendingUpgrades(upgrades);
        } catch (err) {
          console.error("[FETCH] Failed to load upgrade requests:", err);
        }

        // Load waiver documents
        try {
          const suratQuery = query(
            collection(db, "suratPernyataan"),
            where("userId", "==", user.uid)
          );
          const suratSnapshot = await getDocs(suratQuery);
          suratSnapshot.forEach((doc) => {
            const data = doc.data();
            const eventId = data.eventId;
            if (eventId && data.userId === user.uid) {
              const existing = suratData[eventId];
              if (
                !existing ||
                (existing.createdAt &&
                  data.createdAt &&
                  data.createdAt.seconds > existing.createdAt.seconds)
              ) {
                suratData[eventId] = {
                  downloadURL: data.downloadURL,
                  fileName: data.fileName,
                  namaLengkap: data.namaLengkap,
                  createdAt: data.createdAt,
                };
              }
            }
          });
        } catch (err) {
          console.error("[FETCH] Failed to load waiver documents:", err);
        }

        if (isMounted.current) {
          setPesanan(pesananList);
          setSuratPernyataan(suratData);
          setEventTitles(eventTitlesData);
          setEventCategories(eventCategoriesData);
          setCategoryTransferConfigs(categoryTransferConfigsData);
          cachePesananData(user.uid, {
            pesanan: pesananList,
            suratPernyataan: suratData,
            eventTitles: eventTitlesData,
            eventCategories: eventCategoriesData,
            categoryTransferConfigs: categoryTransferConfigsData,
          });
          console.log("[FETCH] Data berhasil diambil:", {
            pesananCount: pesananList.length,
            eventTitles: Object.keys(eventTitlesData),
          });

          // Auto-select: prioritize paid items with approved upgrade first, then payable items
          if (pesananList.length > 0 && !selectedOrderId) {
            const withApprovedUpgrade = pesananList.find(p => {
              const id = p.orderId || p.id;
              return upgrades.some(u => (u.orderId === id || u.sourceOrderId === id) && u.approval_status === "approved");
            });
            const payable = pesananList.find(p => !["settlement", "capture"].includes(p.transaction_status) && (p.administration_status === "qualified" || p.transaction_status === "pending"));
            const chosen = withApprovedUpgrade || payable || pesananList[0];
            setSelectedOrderId(chosen.orderId || chosen.id);
          }
        }
      } catch (err) {
        if (isMounted.current) {
          setError(
            err.code === "permission-denied"
              ? "Access denied. Please ensure you have permission to access the data."
              : err.code === "unavailable"
              ? "Failed to connect to the server. Please check your network."
              : "Failed to load order data. Please try again."
          );
          showNotification(
            "error",
            err.code === "permission-denied"
              ? "Access denied. Please ensure you have permission."
              : err.code === "unavailable"
              ? "Connection failed. Please check your network."
              : "Failed to load order data."
          );
          console.error("[FETCH] Error in fetchPesananDanSurat:", err);
        }
      } finally {
        if (isMounted.current) setLoading(false);
        fetchStatus.current.isFetching = false;
      }
    },
    [user, showNotification]
  );

  const fetchTransaction = useCallback(async () => {
    if (!orderId || fetchStatus.current.isFetching) return;
    fetchStatus.current.isFetching = true;
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${orderId}`
      );
      if (res.data && isMounted.current) {
        console.log("[FETCH] Transaction data:", {
          orderId,
          transaction_time: res.data.transaction_time,
          createdAt: res.data.createdAt,
        });
        setTransaction(res.data);
      }
    } catch (error) {
      console.error("[FETCH] Failed to load transaction status:", error);
      if (isMounted.current) {
        setError(
          error.response?.status === 403
            ? "API access denied. Please check the server configuration."
            : error.response?.status === 404
            ? "Order ID not found."
            : "Failed to fetch transaction data."
        );
        showNotification(
          "error",
          error.response?.status === 403
            ? "API access denied."
            : error.response?.status === 404
            ? "Order ID not found."
            : "Failed to fetch transaction data."
        );
      }
    } finally {
      fetchStatus.current.isFetching = false;
    }
  }, [orderId, showNotification]);

  const handleBayar = useCallback(
    (snapToken) => {
      if (snapToken && window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: async () => {
            showNotification("success", "Payment successful!");
            // Optimistic update or refetch
            setPesanan(prev => prev.map(p => 
                p.snapToken === snapToken ? { ...p, transaction_status: "settlement" } : p
            ));
            fetchPesananDanSurat(true);
          },
          onPending: () => {
            showNotification("warning", "Please continue with the payment.");
            fetchPesananDanSurat(true);
          },
          onError: () => {
            setError("An error occurred during payment.");
            showNotification("error", "An error occurred during payment.");
          },
          onClose: () => {
            console.log("[PAYMENT] Payment window closed.");
          },
        });
      } else {
        setError("Payment token or Midtrans not found.");
        showNotification(
          "error",
          "Payment token or Midtrans not found."
        );
        console.error("[PAYMENT] Missing snapToken or window.snap:", {
          snapToken,
        });
      }
    },
    [showNotification, fetchPesananDanSurat]

  );

  const handleCreateTransaction = async (item) => {
    if (!isCategoryPaymentOpen(item, eventCategories)) {
      showNotification("error", "Registration and payment for this category are closed.");
      return;
    }
    try {
        setLoading(true);
        const idToken = await getIdToken();
        const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/create-transaction`, {
            orderId: item.orderId || item.id,
            eventId: item.eventId,
            voucherCode: appliedVoucher?.code || item.voucherCode // Send applied OR persisted voucher code
        }, { headers: { Authorization: `Bearer ${idToken}` } });
        
        const { snapToken, status } = res.data;
        if (snapToken) {
            handleBayar(snapToken);
        } else if (status === "settlement") {
            showNotification("success", "Payment successful!");
            fetchPesananDanSurat(); // Refresh data
        } else {
            showNotification("error", "Failed to initiate payment.");
        }
    } catch (error) {
        console.error("Create transaction failed:", error);
        showNotification("error", "Failed to process payment: " + (error.response?.data?.message || error.message));
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Please log in to view your orders.");
      return;
    }

    fetchPesananDanSurat(true);

    if (status && !notificationShown) {
      if (status === "settlement") {
        showNotification("success", "Payment successful!");
        setNotificationShown(true);
      } else if (status === "failed") {
        showNotification("error", "Payment failed.");
        setNotificationShown(true);
      }
    }

    if (orderId) {
      if (status) {
         // If redirected back from Midtrans or just registered
         // We rely on fetchPesananDanSurat to get updated status
      }
      fetchTransaction();
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, navigate, orderId, status, showNotification, fetchTransaction]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPesanan = useMemo(() => {
    if (!Array.isArray(pesanan)) {
      console.warn("[FILTER] pesanan bukan array:", pesanan);
      return [];
    }
    return pesanan;
  }, [pesanan]);

  const getEventName = (item) => {
    if (item.type === "shuttle") {
      return `${
        eventTitles[item.eventId] || item.eventId || "Unknown"
      } - Shuttle`;
    }
    return eventTitles[item.eventId] || item.eventId || "Unknown";
  };

  return (
    <section className="min-h-screen bg-white px-4 py-20">
      <div className="max-w-6xl mx-auto">
        <AnimatePresence>
          {error && (
            <div className="bg-red-100 text-red-800 p-4 rounded-xl mb-4">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-5 w-5 mr-2"
              />
              <span>{error}</span>
            </div>
          )}
        </AnimatePresence>
        {loading && filteredPesanan.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-lime-200 border-t-lime-600 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-500 text-lg">Loading registration data...</p>
          </div>
        ) : filteredPesanan.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="bg-slate-50 rounded-full p-4 mb-6">
              <svg className="w-16 h-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Registrations Yet</h3>
            <p className="text-slate-500 text-center max-w-md mb-6">
             Register yourself now to join Jakarta Open Trail Run!
            </p>
            <Button
              onClick={() => navigate("/")}
              variant="primary"
            >
              Register Now
            </Button>
          </div>
        ) : (
          <div className={`flex flex-col md:flex-row gap-4 relative ${filteredPesanan.length > 1 ? "md:items-start" : ""}`}>
            {/* Left Column - Registration Cards */}
            <div className="flex-1 flex flex-col gap-4">
              {filteredPesanan.map((item) => {
                // Define payment status variables
                const isPaid = item.transaction_status === "settlement" || item.transaction_status === "capture";
                const isQualified = item.administration_status === "qualified";
                const isPending = item.transaction_status === "pending";
                const isPaymentOpen = isCategoryPaymentOpen(item, eventCategories);
                const hasApprovedUpgrade = pendingUpgrades.some(u => (u.orderId === (item.orderId || item.id) || u.sourceOrderId === (item.orderId || item.id)) && u.approval_status === "approved");
                const hasPendingUpgrade = pendingUpgrades.some(u => (u.orderId === (item.orderId || item.id) || u.sourceOrderId === (item.orderId || item.id)) && u.approval_status === "pending_approval");
                
                const isSelected = selectedOrderId === (item.orderId || item.id);
                return (
                <div
                  key={`${item.eventId}-${item.id}`}
                  className="bg-white rounded-xl border border-slate-200 p-5"
                  onClick={() => setSelectedOrderId(item.orderId || item.id)}
                >
                  {/* Header: Event Name + Status Badge */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg md:text-xl font-bold text-slate-800 uppercase">
                          {getEventName(item)}
                        </h3>
                      </div>
                      {/* Location */}
                      <div className="text-sm text-lime-600 mt-1 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Cibodas, West Java - Indonesia
                      </div>
                    </div>
                    {/* Status Badge - Moved to Right */}

                  </div>

                  {/* Registration Status Progress Indicator - Event only */}
                  {item.type !== "shuttle" && (
                    <RegistrationStatusIndicator item={item} />
                  )}

                  {/* Shuttle: banner for partial ticket holders */}
                  {item.type === "shuttle" &&
                    (item.transaction_status === "settlement" || item.transaction_status === "capture") &&
                    (item.tripType === "one-way-departure" || item.tripType === "one-way-return") && (
                    <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div>
                        <p className="text-sm font-semibold text-amber-800">
                          {item.tripType === "one-way-departure"
                            ? "You only have a departure ticket"
                            : "You only have a return ticket"}
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          {item.tripType === "one-way-departure"
                            ? "Book your return ticket (Cibodas → Jakarta) to complete your trip."
                            : "Book your departure ticket (Jakarta → Cibodas) to complete your trip."}
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => navigate("/Shuttle")}
                        variant="primary"
                        className="shrink-0"
                      >
                        {item.tripType === "one-way-departure" ? "Book Return Ticket" : "Book Departure Ticket"}
                      </Button>
                    </div>
                  )}

                  {/* Status Description - event only (shuttle has no admin verification flow) */}
                  {item.type !== "shuttle" && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${
                      item.bibTransfer?.transferredOut
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : item.transaction_status === "settlement" || item.transaction_status === "capture"
                        ? "bg-lime-50 text-lime-700 border border-lime-200"
                        : item.administration_status === "rejected"
                        ? "bg-red-50 text-red-700 border border-red-200 hidden"
                        : item.administration_status === "qualified"
                        ? "bg-slate-50 text-slate-600 border border-slate-200"
                        : "bg-slate-50 text-slate-600 border border-slate-200"
                    }`}>
                        {item.bibTransfer?.transferredOut
                          ? "Your BIB has been transferred. This registration is no longer active."
                          : item.transaction_status === "settlement" || item.transaction_status === "capture"
                          ? (hasApprovedUpgrade 
                              ? "Category upgrade approved. Please complete the upgrade payment below."
                              : hasPendingUpgrade
                              ? "Category upgrade request is being reviewed by admin."
                              : "Payment successful. You are registered for this event.")
                          : item.administration_status === "rejected"
                          ? `We're sorry, your registration was rejected. ${item.note ? `Reason: ${item.note}` : ""}`
                          : item.administration_status === "qualified"
                          ? (item.eventId === "ROADTOJOTR2026"
                              ? "Your registration is confirmed. Please complete payment to finish registration."
                              : "Your data has been verified. Please complete payment to finish registration.")
                          : "Your data is being verified by admin. Please wait for confirmation."}
                    </div>
                  )}

                  {item.type === "event" && isPaid && !item.bibTransfer?.transferredOut && isCategoryTransferOpen(item) && !hasPendingUpgrade && !hasApprovedUpgrade && (
                    <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Category transfer is available</p>
                        <p className="text-xs text-slate-500">
                          Change your race category before the transfer deadline.
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => setTransferModal({
                          isOpen: true,
                          item,
                          targetCategory: "",
                        })}
                        variant="primary"
                      >
                        Change Category
                      </Button>
                    </div>
                  )}

                  {item.type === "event" && item.categoryTransfer?.hasTransferred && (
                    <div className="mb-4 rounded-lg border border-lime-200 bg-lime-50 p-3 text-sm text-lime-700">
                      Category changed from {item.categoryTransfer.originalCategory || "-"} to {item.categoryTransfer.currentCategory || item.kategori || "-"}.
                    </div>
                  )}

                    <div className="grid grid-cols-2 md:grid-cols-12 gap-4 mb-4">
                      <div className="col-span-2 md:col-span-4">
                        <div className="text-xs text-slate-400 mb-1">Participant Name</div>
                        <div className="text-base font-semibold text-slate-800">
                          {item.namaLengkap || user?.displayName || "-"}
                        </div>
                      </div>
                      {/* Trip Type or Category */}
                      <div className={`col-span-2 ${item.type === "shuttle" ? "md:col-span-4" : "md:col-span-4"}`}>
                        <div className="text-xs text-slate-400 mb-1">
                          {item.type === "shuttle" ? "Trip Type" : "Category"}
                        </div>
                        <div className="text-base font-semibold text-slate-800 uppercase">
                          {item.type === "shuttle"
                            ? (item.tripType === "round-trip" ? "Round Trip" : item.tripType === "one-way-departure" ? "Departure Only" : item.tripType === "one-way-return" ? "Return Only" : "-")
                            : item.kategori || "-"}
                        </div>
                      </div>
                      {/* Shuttle: show departure/return info; Event: show jersey size */}
                      {item.type === "shuttle" ? (
                        <div className="col-span-2 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">
                            {item.tripType === "one-way-return" ? "Return" : "Departure"}
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {item.tripType === "one-way-return"
                              ? (item.returnTime && item.returnTime !== "-" ? item.returnTime : "-")
                              : (item.departureTime && item.departureTime !== "-" ? item.departureTime : "-")}
                          </div>
                        </div>
                      ) : (
                        <div className="col-span-2 md:col-span-4">
                          <div className="text-xs text-slate-400 mb-1">Jersey Size</div>
                          <div className="text-base font-semibold text-slate-800 uppercase">
                            {item.sizeJersey || "-"}
                          </div>
                        </div>
                      )}
                    </div>

                  {/* Info Grid */}
                  {/* Info Grid - Aligned 12-column strategy */}
                  <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100">
                    <div className="col-span-2 md:col-span-4">
                      <div className="text-xs text-slate-400 mb-1">Order ID</div>
                      <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap break-words">
                        {item.orderId || item.id}
                      </div>
                    </div>
                    {/* Columns aligned with top row */}
                    <div className="col-span-1 md:col-span-4">
                      <div className="text-xs text-slate-400 mb-1">Transaction Time</div>
                      <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap">
                        {formatDate(item.createdAt || item.transaction_time)}
                      </div>
                    </div>
                    <div className="col-span-1 md:col-span-4 text-right md:text-left">
                      <div className="text-xs text-slate-400 mb-1">Status</div>
                      <div className={`text-sm font-medium italic ${
                        item.transaction_status === "settlement" || item.transaction_status === "capture"
                          ? "text-lime-600"
                          : item.administration_status === "rejected"
                          ? "text-amber-500"
                          : item.administration_status === "qualified"
                          ? "text-lime-600"
                          : "text-slate-500"
                      }`}>
                        {item.transaction_status === "settlement" || item.transaction_status === "capture" 
                          ? "Paid" 
                          : item.administration_status === "rejected"
                          ? "Rejected"
                          : item.administration_status === "qualified" 
                          ? "Qualified" 
                          : "Registered"}
                      </div>
                    </div>
                    {/* Category Upgrade Requests Integration */}
                    {pendingUpgrades.filter(u => 
                      (u.orderId === (item.orderId || item.id)) || 
                      (u.sourceOrderId === (item.orderId || item.id))
                    ).map((upgrade) => (
                      <React.Fragment key={upgrade.id}>
                      <div className="col-span-2 md:col-span-12 pt-3 mt-1 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Category Upgrade Request</div>
                          <div className="text-sm font-semibold text-slate-800">
                            {upgrade.fromCategory || "-"} <span className="text-slate-400 font-normal mx-1">→</span> {upgrade.toCategory || "-"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Amount due: Rp {Number(upgrade.originalAmountDue ?? upgrade.amountDue ?? 0).toLocaleString("id-ID")}
                            <span className="opacity-70 ml-1">
                              (Rp {Number(upgrade.priceDifference || 0).toLocaleString("id-ID")} diff + Rp {Number(upgrade.upgradeFee || 0).toLocaleString("id-ID")} fee)
                            </span>
                          </div>
                          {appliedUpgradeVoucher && (
                            <div className="text-xs text-lime-600 mt-1">
                              Payable now: Rp {getUpgradeFinalAmount(upgrade).toLocaleString("id-ID")}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-1">
                          {upgrade.approval_status === "pending_approval" && (
                            <span className="text-xs font-medium italic text-amber-500">Awaiting Admin Approval</span>
                          )}
                          {upgrade.approval_status === "rejected" && (
                            <span className="text-xs font-medium italic text-red-500">Rejected</span>
                          )}
                          {["pending_approval", "approved"].includes(upgrade.approval_status) &&
                            !["settlement", "capture"].includes(upgrade.transaction_status) && (
                            <button
                              type="button"
                              onClick={() => handleCancelUpgrade(upgrade)}
                              disabled={cancellingUpgrade}
                              className="text-xs text-slate-500 underline hover:text-red-600 disabled:opacity-50 whitespace-nowrap"
                            >
                              {cancellingUpgrade ? "Cancelling..." : "Cancel request"}
                            </button>
                          )}
                        </div>
                      </div>
                      {upgrade.approval_status === "approved" &&
                        !["settlement", "capture"].includes(upgrade.transaction_status) &&
                        !(upgrade.transaction_status === "pending" && hasUpgradePaymentDetails(upgrade)) && (
                        <div className="col-span-2 md:col-span-12 pt-3 pb-2 border-t border-slate-100">
                          <div className="mb-4">
                            <label className="text-slate-800 font-medium mb-2 block text-sm">Select Payment Method</label>
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setUpgradePaymentMethod("midtrans");
                                  setSelectedOrderId(item.orderId || item.id);
                                }}
                                className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                  upgradePaymentMethod === "midtrans" && selectedOrderId === (item.orderId || item.id)
                                    ? "border-lime-500 bg-lime-50 text-lime-700 shadow-sm"
                                    : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                                }`}
                              >
                                <span className="text-xs font-black uppercase tracking-widest">BANK</span>
                                <span className="text-[10px] opacity-70">Local Gateway</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setUpgradePaymentMethod("paypal");
                                  setSelectedOrderId(item.orderId || item.id);
                                }}
                                className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                  upgradePaymentMethod === "paypal" && selectedOrderId === (item.orderId || item.id)
                                    ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                    : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                                }`}
                              >
                                <span className="text-xs font-black uppercase tracking-widest">PAYPAL/CC</span>
                                <span className="text-[10px] opacity-70">Global (USD)</span>
                              </button>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <VoucherInput
                              eventId={upgrade.eventId || item.eventId}
                              kategori={upgrade.toCategory || item.kategori}
                              onApplyVoucher={(code, discount, discountType) => {
                                setAppliedUpgradeVoucher({ code, discount, discountType: discountType || "percentage" });
                                setSelectedOrderId(item.orderId || item.id);
                              }}
                              onCodeChange={(val) => {
                                setCurrentUpgradeVoucherInput(val);
                                setSelectedOrderId(item.orderId || item.id);
                              }}
                            >
                              {upgradePaymentMethod === "midtrans" && (
                                <Button
                                  type="button"
                                  onClick={() => handleUpgradePayment(upgrade)}
                                  loading={loading}
                                  disabled={
                                    currentUpgradeVoucherInput &&
                                    currentUpgradeVoucherInput.trim() !== "" &&
                                    (!appliedUpgradeVoucher || appliedUpgradeVoucher.code !== currentUpgradeVoucherInput)
                                  }
                                  className="px-6 md:min-w-[140px]"
                                >
                                  {getUpgradeFinalAmount(upgrade) === 0 ? "Complete Payment" : "Pay Now"}
                                </Button>
                              )}
                              {upgradePaymentMethod === "paypal" && (
                                <Button
                                  type="button"
                                  onClick={() => {
                                    const sidebar = document.getElementById("order-sidebar");
                                    if (sidebar) {
                                      sidebar.scrollIntoView({ behavior: "smooth" });
                                    }
                                  }}
                                  className="md:hidden px-6"
                                >
                                  Pay Now
                                </Button>
                              )}
                            </VoucherInput>
                          </div>
                        </div>
                      )}
                      
                      {/* Show VA Numbers if Upgrade is Pending Payment */}
                      {upgrade.approval_status === "approved" && upgrade.transaction_status === "pending" && hasUpgradePaymentDetails(upgrade) && (
                        <div className="col-span-2 md:col-span-12 mt-2">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            {/* Standard VA Numbers (BCA, BRI, etc) */}
                            {upgrade.va_numbers && upgrade.va_numbers.length > 0 && upgrade.va_numbers.map((va, idx) => (
                              <div key={`va-${idx}`} className="mb-2">
                                <span className="text-xs font-bold text-slate-700">{va.bank?.toUpperCase()} Virtual Account:</span>
                                <div className="font-mono text-lg text-slate-900 select-all">{va.va_number}</div>
                              </div>
                            ))}

                            {/* Permata VA */}
                            {upgrade.permata_va_number && (
                              <div className="mb-2">
                                <span className="text-xs font-bold text-slate-700">PERMATA Virtual Account:</span>
                                <div className="font-mono text-lg text-slate-900 select-all">{upgrade.permata_va_number}</div>
                              </div>
                            )}

                            {/* Mandiri Bill (ECHANNEL) */}
                            {upgrade.bill_key && upgrade.biller_code && (
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs font-bold text-slate-700">MANDIRI Bill Key:</span>
                                  <div className="font-mono text-lg text-slate-900 select-all">{upgrade.bill_key}</div>
                                </div>
                                <div>
                                  <span className="text-xs font-bold text-slate-700">MANDIRI Biller Code:</span>
                                  <div className="font-mono text-lg text-slate-900 select-all">{upgrade.biller_code}</div>
                                </div>
                              </div>
                            )}

                            {hasUpgradePaymentDetails(upgrade) && upgrade.expiry_time && (
                              <div className="text-sm text-red-600 font-medium mt-3">Expires: {formatDate(upgrade.expiry_time)}</div>
                            )}
                          </div>
                        </div>
                      )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* BIB Number Display */}
                  {item.bibNumber && (
                    <div className="py-3 border-t border-slate-100">
                      <div className="inline-block bg-lime-50 border border-lime-200 rounded-lg px-4 py-2 text-center">
                        <div className="text-xs text-slate-500 uppercase">BIB Number</div>
                        <div className="text-2xl font-black text-lime-700">{item.bibNumber}</div>
                        {item.bibName && <div className="text-xs text-slate-600">{item.bibName}</div>}
                      </div>
                    </div>
                  )}

                  {/* VA Payment Info - only for event items with bank transfer */}
                  {item.type !== "shuttle" && item.transaction_status === "pending" &&
                    (item.eventId !== "ROADTOJOTR2026" || (item.va_numbers?.length > 0 || item.permata_va_number || (item.bill_key && item.biller_code))) && (
                    <div className="py-3 border-t border-slate-100">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        {/* Standard VA Numbers (BCA, BRI, etc) */}
                        {item.va_numbers && item.va_numbers.length > 0 && item.va_numbers.map((va, idx) => (
                          <div key={`va-${idx}`} className="mb-2">
                            <span className="text-xs font-bold text-slate-700">{va.bank?.toUpperCase()} Virtual Account:</span>
                            <div className="font-mono text-lg text-slate-900 select-all">{va.va_number}</div>
                          </div>
                        ))}

                        {/* Permata VA */}
                        {item.permata_va_number && (
                          <div className="mb-2">
                            <span className="text-xs font-bold text-slate-700">PERMATA Virtual Account:</span>
                            <div className="font-mono text-lg text-slate-900 select-all">{item.permata_va_number}</div>
                          </div>
                        )}

                        {/* Mandiri Bill (ECHANNEL) */}
                        {item.bill_key && item.biller_code && (
                          <div className="space-y-2">
                            <div>
                              <span className="text-xs font-bold text-slate-700">MANDIRI Bill Key:</span>
                              <div className="font-mono text-lg text-slate-900 select-all">{item.bill_key}</div>
                            </div>
                            <div>
                              <span className="text-xs font-bold text-slate-700">MANDIRI Biller Code:</span>
                              <div className="font-mono text-lg text-slate-900 select-all">{item.biller_code}</div>
                            </div>
                          </div>
                        )}

                        {(item.va_numbers?.length > 0 || item.permata_va_number || (item.bill_key && item.biller_code)) && item.expiry_time && (
                          <div className="text-sm text-red-600 font-medium mt-3">Expires: {formatDate(item.expiry_time)}</div>
                        )}
                        
                        {!(item.va_numbers?.length > 0 || item.permata_va_number || (item.bill_key && item.biller_code)) && (
                          <div className="text-sm text-slate-500 italic">Payment information is being prepared...</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Shuttle Pay Now Button - for pending shuttle with snapToken */}
                  {item.type === "shuttle" && item.transaction_status === "pending" && (
                    <div className="py-3 border-t border-slate-100">
                      <Button
                        type="button"
                        onClick={() => {
                          if (item.snapToken) {
                            handleBayar(item.snapToken);
                          } else {
                            // Create new transaction for shuttle
                            axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/shuttle-create-transaction`, {
                              orderId: item.orderId || item.id
                            }).then(res => {
                              if (res.data.snapToken) handleBayar(res.data.snapToken);
                            }).catch(err => {
                              showNotification("error", "Failed to process payment: " + (err.response?.data?.message || err.message));
                            });
                          }
                        }}
                        loading={loading}
                        className="px-6"
                      >
                        Continue Payment
                      </Button>
                    </div>
                  )}

                  {/* Payment Section - Voucher & Buttons */}
                  {item.type === "event" && !isPaid && (isQualified || isPending) && !isPaymentOpen && (
                    <div className="py-3 border-t border-slate-100">
                      <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
                        Registration and payment for this category are closed.
                      </div>
                    </div>
                  )}

                  {item.type === "event" && !isPaid && (isQualified || isPending) && isPaymentOpen && (
                    <div className="pt-3 pb-2 border-t border-slate-100">
                      {/* Payment Method Selector */}
                      {getFinalPrice(item, appliedVoucher, eventCategories).finalPrice > 0 && (
                        <div className="mb-4">
                          <label className="text-slate-800 font-medium mb-2 block text-sm">Select Payment Method</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                  setPaymentMethod("midtrans");
                                  setSelectedOrderId(item.orderId || item.id);
                              }}
                              className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                paymentMethod === "midtrans" && selectedOrderId === (item.orderId || item.id)
                                  ? "border-lime-500 bg-lime-50 text-lime-700 shadow-sm"
                                  : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                              }`}
                            >
                              <span className="text-xs font-black uppercase tracking-widest">BANK</span>
                              <span className="text-[10px] opacity-70">Local Gateway</span>
                            </button>
                            <button
                              type="button"
                              disabled={isBankPaymentActive(item)}
                              onClick={() => {
                                  setPaymentMethod("paypal");
                                  setSelectedOrderId(item.orderId || item.id);
                              }}
                              className={`flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 transition-all duration-300 ${
                                isBankPaymentActive(item)
                                  ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed opacity-60"
                                  : paymentMethod === "paypal" && selectedOrderId === (item.orderId || item.id)
                                  ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                  : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200"
                              }`}
                            >
                              <span className="text-xs font-black uppercase tracking-widest">PAYPAL/CC</span>
                              <span className="text-[10px] opacity-70">Global (USD)</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Left Side Payment Action - ONLY Midtrans button remains here */}
                      <div className="space-y-4">
                        <VoucherInput
                             eventId={item.eventId}
                             kategori={item.kategori}
                             disabled={isPaid}
                             onApplyVoucher={(code, discount, discountType) => {
                               setAppliedVoucher({ code, discount, discountType: discountType || "percentage" });
                               setSelectedOrderId(item.orderId || item.id);
                             }}
                             onCodeChange={(val) => {
                               setCurrentVoucherInput(val);
                               setSelectedOrderId(item.orderId || item.id);
                             }}
                        >
                            {/* Midtrans Button stays on left */}
                            {paymentMethod === "midtrans" && (
                                <Button
                                    type="button"
                                    onClick={() => handleCreateTransaction(item)}
                                    loading={loading}
                                    disabled={
                                        isPaid || 
                                        (!isQualified && !isPending && !item.snapToken) || 
                                        (currentVoucherInput && currentVoucherInput.trim() !== "" && (!appliedVoucher || appliedVoucher.code !== currentVoucherInput))
                                    }
                                    className="px-6 md:min-w-[140px]"
                                >
                                    {isPaid ? "Already Paid" : (getFinalPrice(item, appliedVoucher, eventCategories).finalPrice === 0 ? "Complete Registration" : "Pay Now")}
                                </Button>
                            )}

                            {/* PayPal Scroll Helper for Mobile */}
                            {paymentMethod === "paypal" && (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        const sidebar = document.getElementById("order-sidebar");
                                        if (sidebar) {
                                            sidebar.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    className="md:hidden px-6"
                                >
                                    Pay Now
                                </Button>
                            )}
                        </VoucherInput>

                      </div>
                    </div>
                  )}


                  {/* Action Buttons - RPC and E-Ticket */}
                 {/*  {item.type === "event" && isPaid && (
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                      <Button
                        type="button"
                        onClick={() => navigate("/RSVPRPC")}
                        className="inline-flex items-center gap-2 px-4 py-2"
                      >
                        <FontAwesomeIcon icon={faLocationDot} /> Pilih Lokasi RPC
                      </Button>
                    </div>
                  )} */}


                </div>
                );
              })}
            </div>

            {/* Right Column - Sidebar Informasi Pesanan */}
            {filteredPesanan.length > 0 && (
              <div id="order-sidebar" className="w-full md:w-1/4 max-w-sm">
                <div className={`bg-white p-4 rounded-xl border border-slate-200 flex flex-col gap-4 ${filteredPesanan.length > 1 ? "sticky top-24 h-fit" : "h-full"}`}>
                  <h2 className="text-lg md:text-xl font-bold text-slate-800 uppercase mb-4 text-center md:text-left">
                    EVENT DETAILS
                  </h2>
                  
                    {(() => {
                        const item = selectedOrderId 
                          ? filteredPesanan.find(p => (p.orderId || p.id) === selectedOrderId) || filteredPesanan[0]
                          : filteredPesanan[0];
                        
                        if (!item) return null;

                        const isQualified = item.administration_status === "qualified";
	                        const isPaid = item.transaction_status === "settlement" || item.transaction_status === "capture";
	                        const isPending = item.transaction_status === "pending";
	                        const isPaymentOpen = isCategoryPaymentOpen(item, eventCategories);
	                        const hasApprovedUpgrade = pendingUpgrades.some(u => (u.orderId === (item.orderId || item.id) || u.sourceOrderId === (item.orderId || item.id)) && u.approval_status === "approved");
	                        const hasPendingUpgrade = pendingUpgrades.some(u => (u.orderId === (item.orderId || item.id) || u.sourceOrderId === (item.orderId || item.id)) && u.approval_status === "pending_approval");
	                        const approvedUpgrade = pendingUpgrades.find(u =>
	                          (u.orderId === (item.orderId || item.id) || u.sourceOrderId === (item.orderId || item.id)) &&
	                          u.approval_status === "approved" &&
	                          !["settlement", "capture"].includes(u.transaction_status) &&
	                          !(u.transaction_status === "pending" && hasUpgradePaymentDetails(u))
	                        );
                        
                        const categories = eventCategories[item.eventId] || [];
                        
                        let categoryDetail = null;
                        
                        if (item.kategori) {
                            categoryDetail = categories.find(c => c.categoryName === item.kategori);
                        }
                        if (!categoryDetail && item.distance) {
                             categoryDetail = categories.find(c => c.distance == item.distance);
                        }
                        if (!categoryDetail && item.kategori) {
                            const itemDistanceMatch = (item.kategori || "").match(/(\d+)/);
                            const itemDistance = itemDistanceMatch ? parseInt(itemDistanceMatch[0]) : null;
                            
                            if (itemDistance) {
                                 categoryDetail = categories.find(c => c.distance == itemDistance);
                            }
                        }

                        if (!categoryDetail && item.kategori) {
                            const k = item.kategori.toUpperCase();
                             if (k.includes("5K") || k.includes("FUN")) categoryDetail = categories.find(c => c.distance == 5);
                             else if (k.includes("10K")) categoryDetail = categories.find(c => c.distance == 10);
                             else if (k.includes("21K") || k.includes("HM")) categoryDetail = categories.find(c => c.distance == 21);
                             else if (k.includes("42K") || k.includes("MARATHON")) categoryDetail = categories.find(c => c.distance == 42);
                             else if (k.includes("75K") || k.includes("ULTRA")) categoryDetail = categories.find(c => c.distance == 75);
                        }

                         const { displayPrice, finalPrice } = getFinalPrice(item, appliedVoucher, eventCategories);

                         const icons = getCategoryIcons(item.kategori, categoryDetail?.distance || item.distance);
                         const { elevationGain, cot } = categoryDetail || {};

                        return (
                          <>
                            <div className="space-y-4">
                              <div className="text-center md:text-left">
                                <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">CATEGORY</div>
                                <h3 className="text-xl font-black text-slate-800 uppercase">
                                  {item.kategori || "-"}
                                </h3>
                              </div>

                               <div className="flex gap-2 justify-center md:justify-start">
                                {icons.icon1Url && (
                                  <img
                                    src={icons.icon1Url}
                                    alt="Icon"
                                    className="h-5 object-contain"
                                  />
                                )}
                                {icons.icon2Url && (
                                  <img
                                    src={icons.icon2Url}
                                    alt="Icon"
                                    className="h-5 object-contain"
                                  />
                                )}
                                {icons.icon3Url && (
                                  <img
                                    src={icons.icon3Url}
                                    alt="Icon"
                                    className="h-5 object-contain"
                                  />
                                )}
                              </div>

                              <div className="flex items-center gap-1.5 md:gap-2 py-4 border-t border-b border-slate-100">
                                  <div className="text-center flex-1">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">ELEVATION</p>
                                    <p className="text-sm font-bold text-slate-700 whitespace-nowrap">
                                      {elevationGain || "-"}M+
                                    </p>
                                  </div>
                                  <div className="h-8 w-px bg-slate-200"></div>
                                  <div className="text-center flex-1">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">CUT OFF</p>
                                    <p className="text-sm font-bold text-slate-700 whitespace-nowrap">
                                      {cot ? `${cot} HOURS` : "- HOURS"}
                                    </p>
                                  </div>
                                  <div className="h-8 w-px bg-slate-200"></div>
                                  <div className="text-center flex-1">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">PRICE</p>
                                    <div>
                                        {appliedVoucher ? (
                                            <>
                                                <p className="text-xs text-rose-500 line-through">
                                                    Rp {displayPrice ? Number(displayPrice).toLocaleString("id-ID") : "0"}
                                                </p>
                                                <p className="text-sm font-bold text-lime-600 whitespace-nowrap">
                                                    Rp {Math.max(0, finalPrice).toLocaleString("id-ID")}
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-sm font-bold text-slate-700 whitespace-nowrap">
                                                Rp {displayPrice ? Number(displayPrice).toLocaleString("id-ID") : "0"}
                                            </p>
                                        )}
                                    </div>
                                  </div>
                              </div>
                            </div>

                            {/* Sidebar Action - ONLY PayPal button moves here */}
	                            {paymentMethod === "paypal" && !isPaid && (isQualified || isPending) && isPaymentOpen && (
	                                <div className="mt-4 pt-4 border-t border-slate-100">
	                                    <PayPalButton 
                                        orderId={item.orderId || item.id}
                                        eventId={item.eventId || "JOTR2026"}
                                        voucherCode={appliedVoucher?.code}
                                        onSuccess={(data) => {
                                            showNotification("success", "PayPal payment successful!");
                                            fetchPesananDanSurat(true);
                                        }}
                                        onError={(msg) => showNotification("error", msg)}
                                    />
	                                </div>
	                            )}

	                            {upgradePaymentMethod === "paypal" && isPaid && approvedUpgrade && (
	                                <div className="mt-4 pt-4 border-t border-slate-100">
	                                    <PayPalButton
	                                        orderId={approvedUpgrade.orderId || approvedUpgrade.id}
	                                        eventId={approvedUpgrade.eventId || item.eventId || "JOTR2026"}
	                                        voucherCode={appliedUpgradeVoucher?.code}
	                                        createOrderPath="/api/category-transfer/paypal/create-order"
	                                        captureOrderPath="/api/category-transfer/paypal/capture-order"
	                                        extraPayload={{ transferOrderId: approvedUpgrade.orderId || approvedUpgrade.id }}
	                                        onSuccess={async () => {
	                                            localStorage.removeItem(`pesanan_${user.uid}`);
	                                            showNotification("success", "Upgrade payment completed. Category will be updated shortly.");
	                                            await fetchPesananDanSurat(true);
	                                        }}
	                                        onError={(msg) => showNotification("error", msg)}
	                                    />
	                                </div>
	                            )}

	                             {isPaid && (
	                                 <div className={`mt-4 text-sm font-medium p-3 rounded-lg text-center md:text-left ${hasApprovedUpgrade || hasPendingUpgrade ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-lime-50 text-lime-600 border border-lime-200'}`}>
                                     {item.bibTransfer?.transferredOut ? "BIB transferred" : hasApprovedUpgrade ? "Upgrade payment pending" : hasPendingUpgrade ? "Upgrade request pending" : "Payment successful"}
                                 </div>
                             )}
                          </>
                        );
                      })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {transferModal.isOpen && transferModal.item && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close category transfer"
            onClick={() => setTransferModal({ isOpen: false, item: null, targetCategory: "" })}
          />
          <div className="relative w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl">
            {(() => {
              const item = transferModal.item;
              const eventId = item.eventId || "JOTR2026";
              const categories = eventCategories[eventId] || [];
              const currentCategory = item.kategori || "";
              const currentDetail = getCategoryByName(eventId, currentCategory);
              const targetDetail = getCategoryByName(eventId, transferModal.targetCategory);
              const cleanPrice = (val) => {
                if (typeof val === "number") return val;
                const cleaned = String(val || "0").replace(/[^0-9]/g, "");
                return parseInt(cleaned, 10) || 0;
              };

              const currentPrice = getCategoryPriceForItem(currentDetail, item) || cleanPrice(item.harga);
              const targetPrice = getCategoryPriceForItem(targetDetail, item);
              const difference = targetDetail ? targetPrice - currentPrice : 0;
              const upgradeFee = difference > 0 ? 100000 : 0;
              const amountDue = difference > 0 ? difference + upgradeFee : 0;

              return (
                <>
                  <div className="flex items-center justify-between border-b border-slate-100 p-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">Change Category</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Order {item.orderId || item.id}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTransferModal({ isOpen: false, item: null, targetCategory: "" })}
                      className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="max-h-[65vh] md:max-h-[75vh] overflow-y-auto space-y-4 p-4">
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-400">Current Category</p>
                      <p className="mt-1 text-base font-bold uppercase text-slate-800">{currentCategory || "-"}</p>
                    </div>

                    <InputField
                      label="New Category"
                      name="targetCategory"
                      type="select"
                      value={transferModal.targetCategory}
                      onChange={(event) => {
                        setTransferModal((prev) => ({ ...prev, targetCategory: event.target.value }));
                        setModalErrors(prev => ({ ...prev, targetCategory: "" }));
                      }}
                      options={[
                        { value: "", label: "Select category" },
                        ...categories
                          .filter((category) => category.categoryName !== currentCategory)
                          .map((category) => ({
                            value: category.categoryName,
                            label: category.categoryName
                          }))
                      ]}
                      required
                      disabled={transferSaving}
                      error={modalErrors.targetCategory}
                    />

                    {difference > 0 && (
                      <div className="space-y-4 pt-2 border-t border-slate-100 mt-4">
                        <div className="bg-lime-50 border border-lime-200 p-3 rounded-lg flex items-center gap-3">
                           <p className="text-xs font-medium text-lime-800 leading-tight">
                              Upgrade requires qualification review. Please provide your latest Strava link and race certificate.
                           </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            label="STRAVA Link"
                            name="stravaLink"
                            placeholder="https://strava.com/activities/..."
                            value={transferModal.stravaLink}
                            onChange={(e) => {
                              setTransferModal(prev => ({ ...prev, stravaLink: e.target.value }));
                              setModalErrors(prev => ({ ...prev, stravaLink: "" }));
                            }}
                            required
                            disabled={transferSaving}
                            error={modalErrors.stravaLink}
                          />

                          <div className="space-y-1">
                            <InputField
                              label="Upload Certificate (Max 5MB)"
                              name="certificate"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleCertificateUpload(file);
                                }
                              }}
                              disabled={transferSaving || uploadingCertificate}
                              error={modalErrors.certificateUrl}
                              labelColor="text-slate-800"
                            />
                            {transferModal.certificateUrl && !uploadingCertificate && (
                              <div className="text-xs text-lime-600 pl-1 mt-[-4px]">
                                File uploaded: <a href={transferModal.certificateUrl} target="_blank" rel="noopener noreferrer" className="underline font-medium">View Certificate</a>
                              </div>
                            )}
                            {uploadingCertificate && (
                              <div className="text-xs text-slate-500 pl-1 mt-[-4px]">
                                Uploading...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {targetDetail && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs font-bold uppercase text-slate-400">Current Price</p>
                            <p className="font-bold text-slate-800">Rp {currentPrice.toLocaleString("id-ID")}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase text-slate-400">New Price</p>
                            <p className="font-bold text-slate-800">Rp {targetPrice.toLocaleString("id-ID")}</p>
                          </div>
                        </div>
                        <div className="mt-3 border-t border-slate-200 pt-3 text-sm text-slate-600">
                          {difference > 0
                            ? `Upgrade payment: Rp ${difference.toLocaleString("id-ID")} difference + Rp ${upgradeFee.toLocaleString("id-ID")} upgrade fee = Rp ${amountDue.toLocaleString("id-ID")}.`
                            : difference < 0
                            ? "Category downgrade. No refund and no downgrade fee."
                            : "No price difference for this category change."}
                        </div>
                      </div>
                    )}

                    <p className="text-xs leading-relaxed text-slate-500">
                      Upgrade changes are confirmed after payment is successful. Downgrades are processed immediately and do not include a refund.
                    </p>
                  </div>

                  <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
                    <Button
                      type="button"
                      onClick={() => {
                        setTransferModal({ isOpen: false, item: null, targetCategory: "", stravaLink: "", certificateUrl: "" });
                        setModalErrors({});
                      }}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCategoryTransfer}
                      disabled={transferSaving || !transferModal.targetCategory || uploadingCertificate}
                      variant="primary"
                    >
                      {transferSaving ? "Saving..." : uploadingCertificate ? "Uploading..." : difference > 0 ? "Pay Upgrade" : "Save Category"}
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
});
